
import React, { useState, useMemo } from 'react';
import { Task, Category, TaskStatus } from '../types';
import { 
  ClipboardList, Search, Filter, ChevronDown, ChevronRight, 
  CheckCircle2, Circle, Clock, Tag, Calendar, MoreVertical, Archive 
} from 'lucide-react';

interface Props {
  tasks: Task[];
  categories: Category[];
  onSelectTask: (task: Task) => void;
}

export const TaskLogPage: React.FC<Props> = ({ tasks, categories, onSelectTask }) => {
  const [groupBy, setGroupBy] = useState<'status' | 'category'>('status');
  const [search, setSearch] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<string[]>(['NEW', 'IN_PROGRESS', 'COMPLETED', 'ARCHIVED', ...categories.map(c => c.id)]);

  const filteredTasks = useMemo(() => {
    return tasks.filter(t => 
      t.title.toLowerCase().includes(search.toLowerCase()) ||
      (t.description || '').toLowerCase().includes(search.toLowerCase())
    );
  }, [tasks, search]);

  const groups = useMemo(() => {
    const grouped: Record<string, Task[]> = {};
    filteredTasks.forEach(task => {
      const key = groupBy === 'category' ? task.category : task.status;
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(task);
    });
    return grouped;
  }, [filteredTasks, groupBy]);

  const toggleGroup = (id: string) => {
    setExpandedGroups(prev => prev.includes(id) ? prev.filter(g => g !== id) : [...prev, id]);
  };

  const getGroupLabel = (key: string) => {
    if (groupBy === 'category') {
      return categories.find(c => c.id === key)?.name || 'Uncategorized Sector';
    }
    return key.replace('_', ' ');
  };

  const formatDuration = (task: Task) => {
    if (!task.completedAt) return 'In Progress';
    const diff = task.completedAt - task.createdAt;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h`;
    return '< 1h';
  };

  return (
    <div className="max-w-7xl mx-auto space-y-12 animate-in fade-in duration-700 pb-20">
      <div className="flex flex-col lg:flex-row justify-between items-end gap-8">
        <div className="space-y-2">
          <h2 className="text-5xl font-black text-vanguard-text tracking-tight uppercase leading-none">Registry</h2>
          <p className="text-[10px] font-bold text-vanguard-muted uppercase tracking-[0.5em]"> Operational History & Log</p>
        </div>
        
        <div className="flex flex-col sm:flex-row items-center gap-6 w-full lg:w-auto">
          <div className="relative flex-1 sm:w-80 group">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-vanguard-muted group-focus-within:text-blue-500 transition-colors" size={18} />
            <input 
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search historical records..."
              className="w-full bg-vanguard-surface border border-vanguard-border rounded-2xl py-4 pl-14 pr-6 text-sm text-vanguard-text focus:outline-none focus:border-blue-500 transition-all font-medium"
            />
          </div>
          
          <div className="flex bg-vanguard-surface border border-vanguard-border rounded-2xl p-1 shadow-sm">
            {(['status', 'category'] as const).map(opt => (
              <button 
                key={opt}
                onClick={() => setGroupBy(opt)}
                className={`px-6 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${groupBy === opt ? 'bg-blue-600 text-white shadow-lg shadow-blue-600/20' : 'text-vanguard-muted hover:text-vanguard-text'}`}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {Object.keys(groups).length === 0 ? (
          <div className="h-96 flex flex-col items-center justify-center space-y-4 opacity-30">
            <ClipboardList size={64} />
            <p className="text-sm font-black uppercase tracking-[0.3em]">No records found</p>
          </div>
        ) : (
          Object.entries(groups).map(([key, groupTasks]) => (
            <div key={key} className="bg-vanguard-surface border border-vanguard-border rounded-[2rem] overflow-hidden shadow-sm transition-colors duration-500">
              <button 
                onClick={() => toggleGroup(key)}
                className="w-full flex items-center justify-between px-8 py-6 hover:bg-black/5 dark:hover:bg-white/5 transition-all"
              >
                <div className="flex items-center gap-4">
                  {expandedGroups.includes(key) ? <ChevronDown size={20} className="text-blue-600" /> : <ChevronRight size={20} className="text-vanguard-muted" />}
                  <span className="text-xs font-black text-vanguard-text uppercase tracking-widest">{getGroupLabel(key)}</span>
                  <span className="px-3 py-1 rounded-full bg-vanguard-bg border border-vanguard-border text-[10px] text-vanguard-muted font-mono font-bold">{(groupTasks as Task[]).length} ITEMS</span>
                </div>
              </button>
              
              {expandedGroups.includes(key) && (
                <div className="divide-y divide-vanguard-border animate-in slide-in-from-top-4 duration-300">
                  {(groupTasks as Task[]).sort((a,b) => b.createdAt - a.createdAt).map(task => (
                    <div 
                      key={task.id}
                      onClick={() => onSelectTask(task)}
                      className={`group flex flex-col sm:flex-row sm:items-center gap-6 px-8 py-6 hover:bg-blue-50/50 dark:hover:bg-blue-900/10 transition-colors cursor-pointer ${task.status === 'ARCHIVED' ? 'opacity-70 grayscale-[0.5]' : ''}`}
                    >
                      <div className="flex items-center gap-4 flex-1">
                        <div className={task.status === 'ARCHIVED' ? 'text-slate-400' : task.isCompleted ? 'text-emerald-500' : 'text-blue-500'}>
                          {task.status === 'ARCHIVED' ? <Archive size={24} /> : (task.isCompleted ? <CheckCircle2 size={24} /> : <Circle size={24} />)}
                        </div>
                        <div className="space-y-1">
                          <h4 className={`text-sm font-bold tracking-tight ${task.status === 'ARCHIVED' ? 'text-slate-400 line-through decoration-slate-300' : task.isCompleted ? 'text-vanguard-muted line-through' : 'text-vanguard-text'}`}>{task.title}</h4>
                          <div className="flex items-center gap-3">
                            <span className="text-[9px] font-black uppercase text-blue-600/70">{task.currentQuadrant}</span>
                            <span className="w-1 h-1 bg-vanguard-border rounded-full" />
                            <span className="text-[9px] font-bold text-vanguard-muted uppercase tracking-wider flex items-center gap-1"><Calendar size={10} /> {new Date(task.createdAt).toLocaleDateString()}</span>
                            {task.status === 'ARCHIVED' && (
                              <>
                                <span className="w-1 h-1 bg-vanguard-border rounded-full" />
                                <span className="text-[8px] font-black uppercase bg-amber-500/10 text-amber-600 px-2 py-0.5 rounded-md border border-amber-500/20">Archived</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between sm:justify-end gap-10">
                        <div className="text-right">
                          <p className="text-[9px] font-black text-vanguard-muted uppercase tracking-widest mb-1">Sector</p>
                          <div className="flex items-center justify-end gap-2">
                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: categories.find(c => c.id === task.category)?.color || '#ccc' }} />
                            <span className="text-[10px] font-bold text-vanguard-text uppercase">{categories.find(c => c.id === task.category)?.name}</span>
                          </div>
                        </div>
                        
                        <div className="text-right min-w-[100px]">
                          <p className="text-[9px] font-black text-vanguard-muted uppercase tracking-widest mb-1">Time Spent</p>
                          <div className="flex items-center justify-end gap-2 text-vanguard-text">
                            <Clock size={12} className="text-blue-500" />
                            <span className="text-[10px] font-black uppercase">{formatDuration(task)}</span>
                          </div>
                        </div>
                        
                        <button className="p-2 text-vanguard-muted hover:text-vanguard-text transition-colors"><MoreVertical size={18} /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};
